import {
  getCrossSellSlugs,
  getTestBySlug,
  isImagingProductSlug,
} from "@/data/tests"
import { isAvailable } from "@/lib/availability"
import { isBookingOpen, type Country } from "@/i18n/config"
import { gateForPath, isAllowedInCountry } from "@/lib/country-gates"

/**
 * Cross-sell slugs for the "made for each other" carousel, filtered to what
 * this market can actually offer. `getCrossSellSlugs` returns the editorial set
 * for a test regardless of market; a card here links to `/tests/<slug>`, so a
 * slug the market can't deliver is a dead or misleading link. Three gates, in
 * the order of the CLAUDE.md gating decision map ("Which gating mechanism?") —
 * none of them a `country === "xx"` literal:
 *
 *   1. `page:` route gate (`COUNTRY_GATES` via `gateForPath`) — drops a
 *      cross-sell whose `/tests/<slug>` route isn't SSG'd in this country
 *      (prostate-mri / pelvic-mri outside CH+NL), which would soft-404.
 *   2. Per-test availability deny-map (`data/tests.ts` `availability`, via
 *      `isAvailable`) — drops a test hidden in this market even though its route
 *      exists. advanced-blood-test is `{ "de-de": false }`: the route still
 *      resolves but 308-redirects to prevention-starter, so a DE cross-sell link
 *      to it lands on a redirect. Same predicate the /tests grid filters on.
 *   3. Imaging category gate (`isBookingOpen(country, "mri")`) — drops the
 *      imaging line-up (full-body MRI + its DEXA / mamma / calcium /
 *      body-composition add-ons) in markets that sell the blood check-up only
 *      (DE, NL): there the MRI category is coming-soon
 *      (`isComingSoon(country, "mri")`), so a "book these together" card can't be
 *      booked. CH is fully launched, so nothing drops there.
 *
 * `gateForPath` alone (what the classic detail page used before) only knows
 * whether the ROUTE exists in the country, so it missed (2) and (3).
 */
export function visibleCrossSellSlugs(
  currentSlug: string,
  country: Country,
  locale: string
): string[] {
  const imagingBookable = isBookingOpen(country, "mri")
  return getCrossSellSlugs(currentSlug).filter((slug) => {
    const gate = gateForPath(`/tests/${slug}`)
    if (gate != null && !isAllowedInCountry(gate, country)) return false
    if (!isAvailable(getTestBySlug(slug)?.availability, country, locale))
      return false
    if (isImagingProductSlug(slug) && !imagingBookable) return false
    return true
  })
}
